/**
 * Data layer for `cap-limits-campaign-publisher-geo-landing-page-levels`. Thin typed fetch wrappers over /api/v1/cap-limits-campaign-publisher-geo-landing-page-levels.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapLimitsCampaignPublisherGeoLandingPageLevelsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCapLimitsCampaignPublisherGeoLandingPageLevelsResponse {
	count: number;
	items: CapLimitsCampaignPublisherGeoLandingPageLevelsRow[];
}

export interface CreateCapLimitsCampaignPublisherGeoLandingPageLevelsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/cap-limits-campaign-publisher-geo-landing-page-levels";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCapLimitsCampaignPublisherGeoLandingPageLevelsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapLimitsCampaignPublisherGeoLandingPageLevelsResponse>(res);
}

export async function create(body: CreateCapLimitsCampaignPublisherGeoLandingPageLevelsBody): Promise<CapLimitsCampaignPublisherGeoLandingPageLevelsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CapLimitsCampaignPublisherGeoLandingPageLevelsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const capLimitsCampaignPublisherGeoLandingPageLevelsQueryKeys = {
	all: ["cap-limits-campaign-publisher-geo-landing-page-levels"] as const,
	list: () => [...capLimitsCampaignPublisherGeoLandingPageLevelsQueryKeys.all] as const,
	detail: (id: string) => [...capLimitsCampaignPublisherGeoLandingPageLevelsQueryKeys.all, id] as const,
};
